"use client";

import { MapPin, Building2, Landmark, Award } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { TenantColors } from "./types";

interface CampaignTypeStepProps {
  onSelect: (type: "SINGLE_LOCATION" | "MULTI_LOCATION") => void;
  selectedType?: "SINGLE_LOCATION" | "MULTI_LOCATION";
  isEditing?: boolean;
  tenantColors?: TenantColors | null;
}

interface CampaignTypeOption {
  value: string;
  icon: LucideIcon;
  title: string;
  description: string;
  comingSoon?: boolean;
}

const TYPES: CampaignTypeOption[] = [
  {
    value: "SINGLE_LOCATION",
    icon: Building2,
    title: "Single Location",
    description:
      "All checkpoints within one venue — perfect for meetups, museum visits, and single-venue events.",
  },
  {
    value: "MULTI_LOCATION",
    icon: MapPin,
    title: "Multi-Location",
    description:
      "Checkpoints spread across multiple locations — ideal for city-wide hunts, conferences, and large events.",
  },
  {
    value: "GALLERY",
    icon: Landmark,
    title: "Gallery / Installation / Museum",
    description:
      "Guide visitors through exhibits or installations — collect digital mementos at each stop.",
    comingSoon: true,
  },
  {
    value: "CERTIFICATE",
    icon: Award,
    title: "Certificate / Credential",
    description:
      "Issue verifiable digital certificates or credentials — ideal for courses, workshops, and achievements.",
    comingSoon: true,
  },
];

export function CampaignTypeStep({
  onSelect,
  selectedType,
  isEditing,
  tenantColors,
}: CampaignTypeStepProps) {
  const accentColor = tenantColors?.accentColor;

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-xl font-semibold text-gray-900">
          {isEditing ? "Campaign Type" : "What type of campaign are you creating?"}
        </h2>
        <p className="mt-1 text-sm text-gray-500">
          {isEditing
            ? "The campaign type cannot be changed after creation."
            : "This determines how locations and maps are configured for your campaign."}
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {TYPES.map((type) => {
          const Icon = type.icon;
          const isSelected = selectedType === type.value;
          const disabled = isEditing || type.comingSoon;
          return (
            <button
              key={type.value}
              onClick={() => !disabled && onSelect(type.value as "SINGLE_LOCATION" | "MULTI_LOCATION")}
              disabled={disabled}
              className={`group relative flex flex-col items-center gap-3 rounded-lg border-2 p-6 text-center transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                type.comingSoon
                  ? "border-gray-100 bg-gray-50 opacity-60 cursor-default"
                  : isEditing
                    ? isSelected
                      ? "border-blue-500 bg-blue-50 cursor-default"
                      : "border-gray-100 bg-gray-50 opacity-40 cursor-not-allowed"
                    : "border-gray-200 hover:border-blue-500 hover:shadow-md"
              }`}
              style={
                !disabled && accentColor
                  ? ({ "--hover-border": accentColor } as React.CSSProperties)
                  : undefined
              }
              onMouseEnter={(e) => {
                if (!disabled && accentColor) {
                  e.currentTarget.style.borderColor = accentColor;
                }
              }}
              onMouseLeave={(e) => {
                if (!disabled && !isSelected) {
                  e.currentTarget.style.borderColor = "";
                }
              }}
            >
              {isEditing && isSelected && (
                <div className="absolute -top-1 -right-1 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              )}
              {type.comingSoon && (
                <span className="absolute top-3 right-3 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-50 text-amber-700 border border-amber-200">
                  Coming Soon
                </span>
              )}
              <div
                className={`flex h-14 w-14 items-center justify-center rounded-full transition-colors ${
                  type.comingSoon
                    ? "bg-gray-100 text-gray-400"
                    : isEditing
                      ? isSelected
                        ? "bg-blue-100 text-blue-600"
                        : "bg-gray-100 text-gray-400"
                      : "bg-blue-50 text-blue-600 group-hover:bg-blue-100"
                }`}
                style={
                  !disabled && accentColor
                    ? {
                        backgroundColor: `${accentColor}15`,
                        color: accentColor,
                      }
                    : undefined
                }
              >
                <Icon className="h-7 w-7" />
              </div>
              <h3 className="text-lg font-medium text-gray-900">
                {type.title}
              </h3>
              <p className="text-sm text-gray-500">{type.description}</p>
            </button>
          );
        })}
      </div>
    </div>
  );
}
